<template>
  <v-card class="pa-6 mb-2">
    <div class="absolute context-menu right mr-2 ml-auto" style="top: 8px;" v-if="!inspectionMode">
      <Approve
        v-if="!award.isApproved && !award.excludedByEditor"
        v-model="award.isApproved"
        targetClass="award"
        :id="award.id"></Approve>
      <DetailContext
        v-if="award.isApproved || award.excludedByEditor"
        targetClass="award"
        :id="award.id"></DetailContext>
    </div>

    <div v-if="award.excludedByEditor">
      <v-chip class="mb-2 grey--text text--darken-2" outlined>
        <v-icon left>mdi-alert-circle-outline</v-icon>
        Auszeichnung redaktionell ausgeschlossen
      </v-chip>
    </div>

    <p class="grey--text text--darken-2 mb-0">{{ award.toplistLabel.rankingGroup.shortCode }} {{ award.period.name }}</p>
    <p class="bold mb-5">{{ award.toplistLabel.value }}</p>
    <p v-if="award.product && award.product.label" class="py-1">
      <v-icon v-if="award.toplistLabel.rankingGroup.shortCode === 'EBK'" class="mr-2">mdi-bike</v-icon>
      <v-icon v-if="award.toplistLabel.rankingGroup.shortCode === 'SMA'" class="mr-2">mdi-cellphone</v-icon>
      <span v-if="award.product.brand">{{ award.product.brand }} </span>{{ award.product.label }} <span v-if="award.product.modelYear">({{ award.product.modelYear }} Modell)</span>
      <!-- Open in new tab -->
      <v-tooltip bottom>
        <template v-slot:activator="{ on, attrs }">
          <v-btn :to="'/product/' + award.product.id" target="_blank" v-on="on" v-bind="attrs" icon>
            <v-icon class="grey--text text--darken-1">mdi-open-in-new</v-icon>
          </v-btn>
        </template>
        <span>Im neuen Tab öffnen</span>
      </v-tooltip>
    </p>

    <InputSwitch
      label="Display Name"
      v-model="award.displayName"
      :entryId="award.id"
      targetClass="award"
      :readonly="readOnlyMode"
      field="displayName"
      :inspectionMode="inspectionMode"
      :data-type="'TYPE_STRING'"
    ></InputSwitch>

    <InputSwitch
      label="Rang"
      v-model="award.rank"
      type="number"
      :entryId="award.id"
      targetClass="award"
      :readonly="readOnlyMode"
      field="rank"
      :inspectionMode="inspectionMode"
      :data-type="'TYPE_INTEGER'"
    ></InputSwitch>

    <InputSwitch
      label="Scoring"
      v-model="award.scoring"
      type="number"
      :entryId="award.id"
      targetClass="award"
      :readonly="readOnlyMode"
      field="scoring"
      :inspectionMode="inspectionMode"
      :data-type="'TYPE_DOUBLE'"
    ></InputSwitch>

    <InputSwitch
      label="Segment / Kategorie"
      v-model="award.segment"
      :entryId="award.id"
      targetClass="award"
      :readonly="readOnlyMode"
      field="segment"
      :inspectionMode="inspectionMode"
      :data-type="'TYPE_STRING'"
    ></InputSwitch>

    <InputSwitch
      label="Sub-Segment / Sub-Kategorie"
      v-model="award.subSegment"
      :entryId="award.id"
      targetClass="award"
      :readonly="readOnlyMode"
      field="subSegment"
      :inspectionMode="inspectionMode"
      :data-type="'TYPE_STRING'"
    ></InputSwitch>

    <InputSwitch
      label="Company Size"
      v-model="award.companySize"
      type="string"
      :entryId="award.id"
      targetClass="award"
      :readonly="readOnlyMode"
      field="companySize"
      :inspectionMode="inspectionMode"
      :data-type="'TYPE_STRING'"
      :selectables="[
        {text: 'k.A.', value: null},
        {text: 'K', value: 'K'},
        {text: 'G', value: 'G'},
        {text: 'XS', value: 'XS'},
        {text: 'S', value: 'S'},
        {text: 'M', value: 'M'},
        {text: 'L', value: 'L'},
        {text: 'XL', value: 'XL'},
        {text: 'XXL', value: 'XXL'},
        {text: 'XXXL', value: 'XXXL'}
      ]"
    ></InputSwitch>

    <div class="text-center" v-if="!showMoreInfoForChanges">
      <v-btn
        v-if="!showMoreInfoToggle"
        v-on:click="showMoreInfoToggle = true"
        class="my-5"
        text>
        Mehr Informationen <v-icon>mdi-chevron-down</v-icon>
      </v-btn>
      <v-btn
        v-if="showMoreInfoToggle"
        v-on:click="showMoreInfoToggle = false"
        class="my-5"
        text>
        Weniger Informationen <v-icon>mdi-chevron-up</v-icon>
      </v-btn>
    </div>

    <div class="grey lighten-4 mb-4" v-show="showMoreInfo">
      <template v-for="field in oldDynamicFields">
        <InputSwitch
          v-bind:key="field.label.label"
          :name="field.label.name"
          :label="field.label.label"
          v-model="field.value"
          :readonly="readOnlyMode"
          :entryId="award.id"
          targetClass="award"
          :field="field.label.name"
          :inspectionMode="inspectionMode"
          :selectables="getSelectables(field)"
          :data-type="field.label.type"
        ></InputSwitch>
      </template>

      <template v-for="field in newDynamicFields">
        <InputSwitch
          v-bind:key="field.label.name"
          :name="field.name"
          :label="field.label.name"
          v-model="field.value"
          :entryId="award.id"
          targetClass="award"
          :readonly="readOnlyMode"
          :field="field.label.name"
          :inspectionMode="inspectionMode"
          :selectables="getSelectables(field)"
        ></InputSwitch>
      </template>
    </div>

    <InputSwitch
      v-model="award.salesforceId"
      label="SalesforceID"
      :entryId="award.id"
      targetClass="award"
      :readonly="readOnlyMode"
      field="salesforceId"
      :inspectionMode="inspectionMode"
    ></InputSwitch>

    <InputSwitch
      v-model="award.excludedFromSalesforce"
      label="state_SF_inaktiv"
      :entryId="award.id"
      targetClass="award"
      :readonly="readOnlyMode"
      field="excludedFromSalesforce"
      :inspectionMode="inspectionMode"
      :selectables="[{text:'true', value: true}, {text:'false', value: false}]"
    ></InputSwitch>

    <v-btn
      v-if="!showSupplier"
      v-on:click="showSupplier = true"
      class="px-0 mx-0 mb-0 grey--text text--darken-2 mono"
      elevation="0"
      color="white">
      Lieferanten <v-icon>mdi-chevron-down</v-icon>
    </v-btn>
    <v-btn
      v-if="showSupplier"
      v-on:click="showSupplier = false"
      class="px-0 mx-0 mb-0 grey--text text--darken-2 mono"
      elevation="0"
      color="white">
      Lieferanten <v-icon>mdi-chevron-up</v-icon>
    </v-btn>
    <div v-show="showSupplier" class="mb-0 pt-3">
      <p v-for="item in award.supplierIds" :key="item.value + item.scope" class="grey--text text--darken-2 small">
        <span class="mb-1">{{ item.supplier.name }} ({{ item.scope }}): </span>
        <span class="mono mb-0">{{ item.value }}</span>
      </p>
    </div>
  </v-card>
</template>

<script>
import { filter } from 'lodash'
import InputSwitch from './InputSwitch'
import Approve from './Approve'
import DetailContext from './DetailContext'

export default {
  props: {
    award: Object,
    readonly: {
      default: false
    },
    inspectionMode: {
      default: false
    }
  },
  components: {
    InputSwitch,
    Approve,
    DetailContext
  },
  data: () => ({
    changes: {},
    showSupplier: false,
    untrackedFields: null,
    showMoreInfoToggle: false
  }),
  created () {
    this.$state.addToRegistry(this.award)
    this.changes = this.$changeService.getPendingChangesByTarget(this.award.id, 'award')
  },
  watch: {
    changes: function () {
      this.doPolyfill()
    }
  },
  methods: {
    doPolyfill () {
      // Make sure dynamic fields that do not exist yet on the entity wll be visible if they have changes.
      // Check polyfill cache.
      if (this.untrackedFields !== null) {
        return
      }
      // No changes will skip the whole process.
      if (Object.keys(this.changes).length === 0) {
        return
      }
      // Collect those MDL attributes that are known already to the award.
      const trackedAttributes = []
      this.award.dynamicFields.forEach(function (mdf) {
        if (Object.prototype.hasOwnProperty.call(mdf, 'label')) {
          trackedAttributes.push(mdf.label.name)
        }
      })
      // Get all attributes that we have changes for.
      const allAttributes = Object.keys(this.changes)
      // Throw away attributes, that are not MDLs.
      const attributes = filter(allAttributes, (attribute) => {
        const nonMDL = ['rank', 'scoring', 'displayName', 'salesforceId', 'excludedFromSalesforce', 'segment', 'subSegment']
        return !nonMDL.includes(attribute)
      })
      // Create cache to collect polyfilled attributes.
      this.untrackedFields = []
      // Go through all change attributes.
      attributes.forEach((attribute) => {
        // The attribute may not be tracked yet.
        if (!trackedAttributes.includes(attribute)) {
          this.untrackedFields.push(attribute)
          // Polyfill MDL
          const polyfill = { value: null, polyfill: true, label: { name: attribute } }
          this.award.dynamicFields.push(polyfill)
        }
      })
    },
    getSelectables: function (field) {
      if (field.label.type === 'TYPE_BOOLEAN') {
        const selectable = [
          {
            text: 'true',
            value: true
          },
          {
            text: 'false',
            value: false
          }
        ]

        if (field.label.nullable === true) {
          selectable.push({
            text: 'k.A.',
            value: null
          })
        }

        return selectable
      }

      return null
    }
  },
  computed: {
    readOnlyMode: function () {
      if (this.readonly === true) {
        return true
      }
      if (this.award.excludedByEditor === true) {
        return true
      }
      if (this.award.isApproved === false) {
        return true
      }
      return false
    },
    newDynamicFields: function () {
      return filter(this.award.dynamicFields, (field) => {
        return typeof field.polyfill !== 'undefined'
      }).sort(function (a, b) {
        return a.label.name.localeCompare(b.label.name)
      })
    },
    oldDynamicFields: function () {
      // Show only fields that are not polyfilled
      return filter(this.award.dynamicFields, (field) => {
        return typeof field.polyfill === 'undefined'
      }).sort(function (a, b) {
        return a.label.label.localeCompare(b.label.label)
      })
    },
    showMoreInfoForChanges: function () {
      return Object.keys(this.changes).length !== 0
    },
    showMoreInfo: function () {
      if (this.showMoreInfoToggle) {
        return true
      }
      if (this.showMoreInfoForChanges) {
        return true
      }
      return false
    }
  }
}
</script>
